import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Routes, Route, Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { topicLabel } from '@gogrov/shared'
import { useStories, useHeadlineDeck } from '@/hooks/useHeadlines'
import { useNewsletterItems } from '@/hooks/useNewsletterItems'
import type { NewsletterItem } from '@/types/library'
import { useIsDesktop, ROUTES } from '@/components/gg/chrome'
import { useEffectiveClient, useEffectiveClientId } from '@/hooks/useAuth'
import {
  useNewsletterAllowed, useBrandKits, useDesigns, useIssues, useSponsors, useAdSlots, useBookings, useYourList, useListStats,
  useNlSave, useNlDelete, useSyncSlots, useSendIssue, uploadNewsletterImage, useSending, isEmail,
  useCreateMediaKit, useTestIssue, issueHash, money, type Issue, type Design, type Booking, type AdSlot, type Sponsor, type MediaKitSnapshot,
} from '@/hooks/useNewsletter'
import {
  renderEmail, renderText, inboxCheck, isDark, HEADER_COLORS, CREAM, UNSUB_PLACEHOLDER, DEFAULT_BRAND, DEFAULT_CONFIG, FAMILY_INFO, EMAIL_FONTS, SHAPES, lockedSlots, magazineRows, ownStories,
  bulletinSections, creativeFit, spotPlace, BULLETIN_SPOTS, type Family, type NewsletterConfig, type BrandKit, type BannerOpts,
  type EmailStory, type AdFill, type MagRow, type AdShape,
} from '@/lib/newsletterEmail'
import type { Story, Headline } from '@/types/database'
import { NewsletterSettings } from '@/pages/newsletter/NewsletterSettings'
import { NewsletterPhone } from '@/pages/newsletter/NewsletterPhone'
import { sendGate, exampleNote } from '@/pages/newsletter/sendGate'
import { noDashes } from '@/lib/text'
import '@/styles/newsletter.css'

const FAMILIES: Family[] = ['letter', 'magazine', 'bulletin']
const famName = (f: Family) => FAMILY_INFO[f].name

function toEmailStory(s: Story): EmailStory {
  return {
    id: s.id, headline: noDashes(s.headline), summary: noDashes(s.summary) ?? null, article: s.article, topic: s.category,
    topicLabel: topicLabel(s.category), source_url: s.source_url,
    source_name: s.source_name, place: s.place_tag,
  }
}

/** A headline from this week's brief, standing in as an example story until
 *  the creator has stories of their own. Send won't take it. */
function toExampleStory(h: Headline): EmailStory {
  const topic = h.topic ?? 'civic'
  return {
    id: `example-${h.id}`, headline: noDashes(h.headline), summary: noDashes(h.summary) ?? null, article: null, topic,
    topicLabel: topicLabel(topic), source_url: h.source_url, source_name: h.source_name, place: h.place ?? null,
  }
}

// Brian's date words (newsletter.html issueDateLong, issueDayShort, shortDate).
const longDate = (d: Date) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
const dayShort = (d: Date) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
const isoDate = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d) }
const shortDate = (iso: string) => iso ? isoDate(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''
const fullDate = (iso: string) => isoDate(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }
/** Brian's addPhoto(): a story photo is shrunk to 1200px wide as a JPEG, the
 *  size the email needs, before it is uploaded to GoGrov's image host. */
async function shrinkPhoto(f: File): Promise<File> {
  if (!/^image\/(jpeg|png|webp)$/.test(f.type)) return f
  try {
    const bmp = await createImageBitmap(f)
    if (bmp.width <= 1200 && f.size < 600_000) return f
    const w = Math.min(1200, bmp.width), h = Math.round(bmp.height * w / bmp.width)
    const c = document.createElement('canvas'); c.width = w; c.height = h
    c.getContext('2d')!.drawImage(bmp, 0, 0, w, h)
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/jpeg', 0.82))
    return blob ? new File([blob], f.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : f
  } catch { return f }
}
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '·'

/** Scales an email to whatever width the pane has (600px wide at send, 375 for the phone view). */
/** A reader's dark mode, the way the Gmail and Outlook apps flip an email:
 *  invert the page, then put the photos back (Brian's log #010). Apple Mail
 *  keeps the colours, since the email asks for light. */
const DARK_SIM = '<style>html{filter:invert(1) hue-rotate(180deg);background:#fff}img{filter:invert(1) hue-rotate(180deg)}</style>'
const darken = (html: string) => html.replace('</head>', `${DARK_SIM}</head>`)

function EmailFrame({ html, maxHeight, width = 640 }: { html: string; maxHeight?: number; width?: number }) {
  const wrap = useRef<HTMLDivElement>(null)
  const frame = useRef<HTMLIFrameElement>(null)
  const [scale, setScale] = useState(1)
  const [height, setHeight] = useState(800)
  useEffect(() => {
    const el = wrap.current
    if (!el) return
    const ro = new ResizeObserver(() => setScale(Math.min(1, el.clientWidth / width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [width])
  return (
    <div ref={wrap} className="nl-frame" style={{ height: maxHeight ? Math.min(maxHeight, height * scale) : height * scale }}>
      <iframe
        ref={frame}
        title="Newsletter preview"
        srcDoc={html}
        sandbox="allow-same-origin"
        style={{ width, height, transform: `scale(${scale})` }}
        onLoad={() => {
          const d = frame.current?.contentDocument
          if (d) setHeight(Math.max(400, d.documentElement.scrollHeight))
        }}
      />
    </div>
  )
}

/** Brian's toast, with Undo when there is something to undo. */
function useToast() {
  const [t, setT] = useState<{ text: string; undo?: () => void } | null>(null)
  const timer = useRef<number>(0)
  const show = useCallback((text: string, undo?: () => void) => {
    window.clearTimeout(timer.current)
    setT({ text, undo })
    timer.current = window.setTimeout(() => setT(null), 4200)
  }, [])
  useEffect(() => () => window.clearTimeout(timer.current), [])
  const node = t ? (
    <div className="nls-toast nl-toast" role="status">
      <span>{t.text}</span>
      {t.undo && <button type="button" onClick={() => { t.undo!(); setT(null) }}>Undo</button>}
    </div>
  ) : null
  return { show, node }
}

/** A modal over the newsletter: a tap outside it or Esc closes it. */
function Modal({ label, onClose, wide, children }: { label: string; onClose: () => void; wide?: boolean; children: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [onClose])
  return (
    <div className="nl-dialog" role="dialog" aria-label={label} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className={`nl-dialog__card${wide ? ' nl-dialog__card--wide' : ''}`}>{children}</div>
    </div>
  )
}
function ModalHead({ title, sub, onClose }: { title: string; sub?: string; onClose: () => void }) {
  return (
    <div className="nl-mhd">
      <b>{title}</b>
      {sub && <span>{sub}</span>}
      <button type="button" className="nl-x" aria-label="Close" onClick={onClose}>×</button>
    </div>
  )
}

/** "Preview: The Letter", at send width and at the phone's. */
function PreviewModal({ family, html, onClose }: { family: Family; html: string; onClose: () => void }) {
  const [dark, setDark] = useState(false)
  const shown = dark ? darken(html) : html
  return (
    <Modal label={`Preview: ${famName(family)}`} onClose={onClose} wide>
      <div className="nl-mhd">
        <b>Preview: {famName(family)}</b>
        <span>{dark
          ? 'Dark mode, the way the Gmail and Outlook apps flip it. Apple Mail keeps your colours. Check your header and logo still read.'
          : 'Rendered from your current queue. Previewing never changes your draft.'}</span>
        <span className="nl-pills nl-mhd__mode" title="How it looks to a reader whose phone or computer is in dark mode">
          <Pill on={!dark} onClick={() => setDark(false)}>Light</Pill>
          <Pill on={dark} onClick={() => setDark(true)}>Dark</Pill>
        </span>
        <button type="button" className="nl-x" aria-label="Close" onClick={onClose}>×</button>
      </div>
      <div className="nl-prev">
        <div><div className="nl-prev__lbl">Desktop · 600px</div><EmailFrame html={shown} /></div>
        <div className="nl-prev__m"><div className="nl-prev__lbl">Mobile · 375px</div><EmailFrame html={shown} width={375} /></div>
      </div>
    </Modal>
  )
}

/**
 * "See it as it will arrive" (Brian's nl-as-sent.js, Oct 29): the email itself,
 * as the send function hands it to Resend, not the builder's preview. Computer
 * and phone widths, Light / Dark, the inbox line, the inbox check, and the
 * request body for the developers.
 */
function AsSentModal({ html, from, sendsFrom, replyTo, subject, preheader, onClose }: {
  html: string; from: string; sendsFrom: string; replyTo: string | null; subject: string; preheader: string; onClose: () => void
}) {
  const [phone, setPhone] = useState(false)
  const [dark, setDark] = useState(false)
  const [copied, setCopied] = useState(false)
  const sample = html.split(UNSUB_PLACEHOLDER).join('#unsubscribe')
  const probs = inboxCheck(html)
  const kb = Math.round(new Blob([html]).size / 1024)
  const copy = () => {
    const body = { endpoint: 'POST https://api.resend.com/emails/batch', body: [{
      from: `${from} <${sendsFrom}>`, to: ['reader@example.com'], reply_to: replyTo ?? undefined, subject,
      html: '(the email above, with this reader’s own unsubscribe link)',
      headers: { 'List-Unsubscribe': '<https://.../functions/v1/newsletter-public?a=unsub&t=...>', 'List-Unsubscribe-Post': 'List-Unsubscribe=One-Click' },
    }] }
    try { void navigator.clipboard.writeText(JSON.stringify(body, null, 2)) } catch { /* no clipboard */ }
    setCopied(true); window.setTimeout(() => setCopied(false), 1600)
  }
  return (
    <Modal label="The email as it will arrive" onClose={onClose} wide>
      <div className="nl-as">
        <div className="nl-as__pv">
          <div className="nl-as__bar">
            <b>As it will arrive</b><span className="nl-as__sp" />
            <span className="nl-pills">
              <Pill on={!phone} onClick={() => setPhone(false)}>Computer</Pill>
              <Pill on={phone} onClick={() => setPhone(true)}>Phone</Pill>
            </span>
            <span className="nl-pills" title="How it looks to a reader whose phone or computer is in dark mode">
              <Pill on={!dark} onClick={() => setDark(false)}>Light</Pill>
              <Pill on={dark} onClick={() => setDark(true)}>Dark</Pill>
            </span>
            <button type="button" className="gg-btn gg-btn--sm" onClick={onClose}>Close</button>
          </div>
          <div className={`nl-as__stage${dark ? ' is-dark' : ''}`}><EmailFrame key={phone ? 'p' : 'd'} html={dark ? darken(sample) : sample} width={phone ? 390 : 640} /></div>
        </div>
        <div className="nl-as__side">
          <div><h4>In the inbox</h4>
            <div className="nl-as__inbox"><div className="nl-as__f">{from}<span>now</span></div><div className="nl-as__s">{subject || 'Add a subject line'}</div><div className="nl-as__p">{preheader}</div></div>
            <p className="nl-as__fine">From {sendsFrom}{replyTo ? ` · replies to ${replyTo}` : ''}</p>
          </div>
          <div><h4>Inbox check</h4>
            <ul className="nl-as__ul">
              {probs.length ? probs.map((p) => <li key={p} className="is-bad">{p}</li>) : <li>Ready for Gmail, Outlook and Apple Mail</li>}
              <li>Unsubscribe link and your mailing address at the foot</li>
              <li>{kb} KB (Gmail clips over about 100 KB)</li>
            </ul>
          </div>
          {dark && <p className="nl-as__fine">Dark mode: Gmail and Outlook apps flip light backgrounds dark and light words dark-on-light, roughly like this; photos stay as they are. Apple Mail keeps your colours (the email asks for light). Check that your header colour and logo still read.</p>}
          <p className="nl-as__fine">This is the email itself, not the builder’s preview: one 600px column, your fonts with a safe fallback, no hover or drag. What you see here is what your readers get.</p>
          <div><h4>For the developers</h4>
            <p className="nl-as__fine">The request GoGrov sends to Resend for each reader, as JSON.</p>
            <button type="button" className="gg-btn gg-btn--sm gg-btn--amber" onClick={copy}>{copied ? 'Copied' : 'Copy for Resend'}</button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

/** A row of choice pills (Brian's pill buttons). */
function Pill({ on, onClick, disabled, children, title }: { on?: boolean; onClick: () => void; disabled?: boolean; children: ReactNode; title?: string }) {
  return <button type="button" className={`nl-pill${on ? ' is-on' : ''}`} aria-pressed={!!on} onClick={onClick} disabled={disabled} title={title}>{children}</button>
}

export function Newsletter() {
  const { data: allowed, isLoading } = useNewsletterAllowed()
  if (isLoading) return <div className="gg-legacy"><span className="gg-spinner" /></div>
  if (!allowed) {
    return (
      <div className="gg-legacy nl">
        <span className="eyebrow eyebrow--accent">Newsletter</span>
        <h1 className="nl__title">Your stories, in their inbox</h1>
        <div className="gg-panel nl-locked">
          <p>
            The newsletter turns the stories you build into an email your audience subscribes to: three layouts,
            your own brand, and sponsor spots you sell directly. Sponsors pay you; GoGrov takes no cut.
          </p>
          <p className="gg-help">It is included on the Grove and Harvest plans. If you are on Sprout and want it, ask us and we will switch it on for your account.</p>
          <Link className="gg-btn gg-btn--amber" to="/contact">Ask about the newsletter</Link>
        </div>
      </div>
    )
  }
  return <NewsletterRoutes />
}

/**
 * Building and sending happen on the computer (Brian's nl-phone.html, Oct 21:
 * "it's best displayed on a desktop device"); the phone shows the read-only
 * summary. The media kit and connect screens stay reachable on both.
 */
function NewsletterRoutes() {
  const desktop = useIsDesktop()
  if (!desktop) return <NewsletterPhone />
  return (
    <Routes>
      <Route index element={<Gallery />} />
      <Route path="build/:family" element={<Builder />} />
      <Route path="brand" element={<BrandStudio />} />
      <Route path="sponsors" element={<SponsorDesk />} />
      <Route path="connect" element={<NewsletterSettings />} />
    </Routes>
  )
}

/** A story the creator sent with ＋ Newsletter: its newsletter version (a
 *  title and a 50 to 90 word attributed blurb), from newsletter_items. */
function toItemStory(i: NewsletterItem): EmailStory {
  const topic = i.topic ?? 'civic'
  return {
    id: i.story_id ?? `item-${i.id}`, headline: noDashes(i.title), summary: noDashes(i.blurb) ?? null, article: null, topic,
    topicLabel: topicLabel(topic), source_url: i.link, source_name: i.source_name, place: i.place,
  }
}

/** The issue: this week's ＋ Newsletter items first (Brian: the issue is only
 *  what was sent to it); without any, the creator's built stories; without
 *  those, example stories from this week's brief, labelled as examples. */
function useIssueStories(): { stories: EmailStory[]; example: boolean } {
  const { data: stories } = useStories()
  const { data: deck } = useHeadlineDeck()
  const { items } = useNewsletterItems()
  return useMemo(() => {
    if (items.length) return { stories: items.map(toItemStory), example: false }
    const own = (stories ?? []).filter((s) => s.status !== 'skipped').map(toEmailStory)
    if (own.length || !deck?.length) return { stories: own, example: false }
    return { stories: deck.slice(0, 8).map(toExampleStory), example: true }
  }, [stories, deck, items])
}

/** What the builder lays out comes back on the next visit (Brian: per
 *  account, gogrov.nl.builder.<account>). Saved designs live in the database. */
interface BuilderDraft { family: Family; config: NewsletterConfig; name: string; brandId: string | null; designId: string | null }
const draftKey = (clientId: string | null) => `gogrov.nl.builder.${clientId ?? 'me'}`
function readDraft(clientId: string | null): BuilderDraft | null {
  try { const raw = localStorage.getItem(draftKey(clientId)); return raw ? JSON.parse(raw) as BuilderDraft : null } catch { return null }
}
function writeDraft(clientId: string | null, d: BuilderDraft) {
  try { localStorage.setItem(draftKey(clientId), JSON.stringify(d)); return true } catch { return false }
}

/** The issue number and date the masthead prints (Brian's Banner details). */
function stamped(config: NewsletterConfig, sent: number): NewsletterConfig {
  const n = config.issueAuto === false ? (config.issueNo || sent + 1) : sent + 1
  const d = config.dateAuto === false && config.sendDate ? isoDate(config.sendDate) : new Date()
  return { ...config, issueLabel: `Issue ${n}`, dateLabel: longDate(d), issueNo: n }
}

/** The gallery's order and words (Brian's newsletter.html v2.4). */
const GALLERY: Family[] = ['letter', 'magazine', 'bulletin']
const GALLERY_WORDS: Record<Family, { line: string; chips: string[] }> = {
  letter: { line: 'Reads like a personal note from you. Best when you write a short intro each week.', chips: ['3–6 stories', 'serif · text-first', 'no ads'] },
  magazine: { line: 'Fully customizable, row by row. Set each story\'s photo position and width, and lock ad space where you want it.', chips: ['4–12 stories', 'editorial layout', 'locked ad placement'] },
  bulletin: { line: 'Sectioned like a local paper, with room for sponsors.', chips: ['4–10 stories', 'sections', 'fixed banner ads 2–6'] },
}

function Gallery() {
  const client = useEffectiveClient()
  const { stories, example } = useIssueStories()
  const { data: issues } = useIssues()
  const { data: yourList } = useYourList()
  const { data: stats } = useListStats()
  const { data: brands } = useBrandKits()
  const brand = brands?.find((b) => b.is_default) ?? brands?.[0] ?? DEFAULT_BRAND
  const navigate = useNavigate()
  const [preview, setPreview] = useState<Family | null>(null)
  const sent = (issues ?? []).filter((i) => i.status === 'sent').length

  const html = (f: Family) => {
    const list = stories.slice(0, FAMILY_INFO[f].stories[0] + 1)
    const cfg: NewsletterConfig = stamped({ ...DEFAULT_CONFIG, note: 'This week in town, the short version.', noteOn: f !== 'bulletin', storyIds: list.map((s) => s.id) }, sent)
    return renderEmail({
      family: f, config: cfg, brand, stories: list,
      publication: client?.display_name ?? 'Your publication', place: client?.city_state ?? '', ads: {},
      sender: { fromName: yourList?.from_name || client?.display_name || '', address: yourList?.mailing_address ?? null },
    })
  }

  return (
    <div className="gg-legacy nl">
      <h1 className="nl__title">Your newsletter</h1>
      <p className="nl__sub">Pick how your newsletter reads. You can switch anytime. Nothing is lost.</p>
      {example && (
        <p className="nl-example">
          An example issue from this week’s brief. Your issue fills with the stories you send with <b>＋ Newsletter</b>; each one is rewritten for the newsletter when you send it.
        </p>
      )}
      <div className="nl-gallery">
        {GALLERY.map((f) => {
          const open = () => navigate(`/app/newsletter/build/${f}`)
          return (
            <div key={f} className="nl-card">
              {f === 'letter' && <span className="nl-card__rec">Recommended first issue</span>}
              <button type="button" className="nl-card__thumb" onClick={open} title={`Open ${famName(f)}`}><EmailFrame html={html(f)} maxHeight={260} /></button>
              <strong>{famName(f)}</strong>
              <span>{GALLERY_WORDS[f].line}</span>
              <span className="nl-card__chips">{GALLERY_WORDS[f].chips.map((c) => <em key={c}>{c}</em>)}</span>
              <div className="nl-card__btns">
                <button type="button" className="gg-btn" onClick={() => setPreview(f)}>Preview</button>
                <button type="button" className="gg-btn gg-btn--amber nl-card__go" onClick={open}>Start with {famName(f).replace(/^The /, 'the ')}</button>
              </div>
            </div>
          )
        })}
      </div>
      <div className="nl-tools">
        <Link className="nl-tool" to="/app/newsletter/brand"><strong>Brand studio</strong><span>The painter's corner. Your colors, fonts, logo and header live here as kits, applied across all three newsletter styles.</span><em>Open Brand studio →</em></Link>
        <Link className="nl-tool" to="/app/newsletter/sponsors"><strong>Sponsor desk</strong><span>Your advertisers and their creatives live here. Sponsors pay you directly. GoGrov takes no cut, and no email service we connect to ever will.</span><em>Open Sponsor desk →</em></Link>
        <div className="nl-tool nl-tool--pub">
          <strong>Publishing</strong>
          <span>The issue sends from GoGrov, to the list you keep in <Link to="/app/newsletter/connect">Your list</Link>{stats?.subscribed ? ` (${stats.subscribed.toLocaleString()} people)` : ''}. Sponsors pay you directly: GoGrov takes no cut.</span>
          <em>sends from GoGrov</em>
        </div>
      </div>

      {/* Not in Brian's gallery: the issues this account has really sent, and its drafts. */}
      {(issues?.length ?? 0) > 0 && (
        <section className="nl-section">
          <h2 className="eyebrow">Sent and drafts</h2>
          <div className="nl-list">
            {issues!.map((i) => (
              <div key={i.id} className="nl-list__row">
                <span><b>{i.subject || 'No subject yet'}</b> · {famName(i.family)}</span>
                <span className={`nl-status nl-status--${i.status}`} title={i.error ?? undefined}>
                  {i.status === 'sent' ? `Sent ${new Date(i.sent_at!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : i.status}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
      {preview && <PreviewModal family={preview} html={html(preview)} onClose={() => setPreview(null)} />}
    </div>
  )
}

/** Today's live booking on each of a design's spots, as the email fills them. */
function useAdFills(slots: AdSlot[] | undefined, bookings: Booking[] | undefined, sponsors: Sponsor[] | undefined, designId: string | null, mode: 'email' | 'kit') {
  return useMemo(() => {
    const out: Record<string, AdFill> = {}
    const now = today()
    for (const s of (slots ?? []).filter((x) => x.design_id === designId)) {
      const live = (bookings ?? []).find((b) => b.slot_id === s.id && b.status !== 'cancelled' && b.starts_on <= now && b.ends_on >= now)
      out[s.slot_key] = live
        ? { slotKey: s.slot_key, shape: s.shape, imageUrl: live.creative_url, sponsor: sponsors?.find((p) => p.id === live.sponsor_id)?.name ?? null }
        : { slotKey: s.slot_key, shape: s.shape, price: mode === 'kit' && s.rate_cents ? `${money(s.rate_cents)} an issue` : null }
    }
    return out
  }, [slots, bookings, sponsors, designId, mode])
}

const GRIP = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
const LINK = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></svg>

function Builder() {
  const { family: fam } = useParams<{ family: Family }>()
  const family: Family = FAMILIES.includes(fam as Family) ? fam as Family : 'letter'
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const client = useEffectiveClient()
  const clientId = useEffectiveClientId()
  const { stories, example } = useIssueStories()
  const { data: designs } = useDesigns()
  const { data: issues } = useIssues()
  const { data: brands } = useBrandKits()
  const { data: slots } = useAdSlots()
  const { data: bookings } = useBookings()
  const { data: sponsors } = useSponsors()
  const { data: list } = useYourList()
  const { data: stats } = useListStats()
  const { data: sending } = useSending()
  const saveDesign = useNlSave<Partial<Design>>('newsletter_designs', 'nl-designs')
  const delDesign = useNlDelete('newsletter_designs', 'nl-designs')
  const syncSlots = useSyncSlots()
  const send = useSendIssue()
  const testSend = useTestIssue()
  const listSize = stats?.subscribed ?? 0
  const town = (client?.city_state ?? '').split(',')[0].trim()
  const toast = useToast()

  const [designId, setDesignId] = useState<string | null>(params.get('design'))
  const [name, setName] = useState('')
  const [brandId, setBrandId] = useState<string | null>(null)
  const [config, setConfig] = useState<NewsletterConfig>({ ...DEFAULT_CONFIG, note: exampleNote(town) })
  const [sel, setSel] = useState<string | null>(null)
  const [linkOpen, setLinkOpen] = useState<string | null>(null)
  const [linkDraft, setLinkDraft] = useState('')
  const [compose, setCompose] = useState<{ title: string; body: string } | null>(null)
  const [modal, setModal] = useState<'preview' | 'send' | 'asSent' | null>(null)
  const [progress, setProgress] = useState<string | null>(null)
  const [subject, setSubject] = useState('')
  const [preheader, setPreheader] = useState('')
  const [sendMsg, setSendMsg] = useState<{ ok?: string; err?: string } | null>(null)
  const [photoBusy, setPhotoBusy] = useState<string | null>(null)
  const [savedDraft, setSavedDraft] = useState(false)
  const loaded = useRef(false)
  const dragId = useRef<string | null>(null)

  const sent = (issues ?? []).filter((i) => i.status === 'sent').length
  // The draft this issue is being tested and sent as: the newest unsent issue
  // of this layout, so a test survives a reload.
  const [draftId, setDraftId] = useState<string | null>(null)
  const draftPicked = useRef(false)
  useEffect(() => {
    if (draftPicked.current || !issues) return
    draftPicked.current = true
    const d = issues.find((i) => (i.status === 'draft' || i.status === 'failed') && i.family === family && i.design_id === designId)
    if (d) { setDraftId(d.id); if (d.subject) setSubject(d.subject); if (d.preheader) setPreheader(d.preheader) }
  }, [issues, family, designId])
  const draft: Issue | undefined = issues?.find((i) => i.id === draftId)

  function applyDesign(d: Design, say = false) {
    setDesignId(d.id); setName(d.name); setConfig({ ...DEFAULT_CONFIG, ...d.config }); setBrandId(d.brand_kit_id); setSel(null)
    if (d.family !== family) navigate(`/app/newsletter/build/${d.family}?design=${d.id}`, { replace: true })
    if (say) toast.show(`Loaded “${d.name}”.`)
  }

  // Load a saved design, else the layout from the last visit, else the newest stories.
  useEffect(() => {
    if (loaded.current) return
    if (designId) {
      if (!designs) return
      const d = designs.find((x) => x.id === designId)
      if (d) { applyDesign(d); loaded.current = true; return }
    }
    const kept = readDraft(clientId)
    if (kept && !designId) {
      setConfig({ ...DEFAULT_CONFIG, ...kept.config }); setName(kept.name); setBrandId(kept.brandId); setDesignId(kept.designId)
      loaded.current = true; setSavedDraft(true)
      return
    }
    if (stories.length) {
      const [min] = FAMILY_INFO[family].stories
      setConfig((c) => ({ ...c, storyIds: stories.slice(0, Math.max(min, Math.min(6, stories.length))).map((s) => s.id) }))
      loaded.current = true
    }
  }, [designId, designs, stories, family, clientId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loaded.current) return
    setSavedDraft(writeDraft(clientId, { family, config, name, brandId, designId }))
  }, [config, name, brandId, designId, family, clientId])

  const brand: BrandKit = brands?.find((b) => b.id === brandId) ?? brands?.find((b) => b.is_default) ?? brands?.[0] ?? DEFAULT_BRAND
  const pool = useMemo(() => [...ownStories(config), ...stories], [config, stories])
  const chosen = config.storyIds.map((id) => pool.find((s) => s.id === id)).filter(Boolean) as EmailStory[]
  const avail = pool.filter((s) => !config.storyIds.includes(s.id))
  const [min, max] = FAMILY_INFO[family].stories
  const fills = useAdFills(slots, bookings, sponsors, designId, 'email')
  const sendConfig = stamped({ ...config, noteOn: family === 'letter' ? true : config.noteOn, toc: config.toc !== false }, sent)
  const issueNo = sendConfig.issueNo!
  // Brian's sendCtx(): the preview text defaults to the first three headlines.
  const pre = preheader.trim() || chosen.slice(0, 3).map((s) => s.headline).join(' · ')
  const fromName = list?.from_name || client?.display_name || 'Your publication'
  const renderIn = {
    family, config: sendConfig, brand, stories: chosen, publication: client?.display_name ?? 'Your publication',
    place: client?.city_state ?? '', ads: fills, replyTo: list?.reply_to,
    sender: { fromName, address: list?.mailing_address ?? null }, subject: subject.trim(), preheader: pre,
  }
  const html = renderEmail(renderIn)
  const sendsFrom = `${list?.handle ?? 'you'}@${sending?.domain || 'news.gogrov.com'}`
  const rows = family === 'magazine' ? magazineRows(config, chosen.length) : []

  // Which magazine row each placed story sits in.
  const rowOf = useMemo(() => {
    const out = new Map<string, number>()
    let q = 0
    rows.forEach((r, ri) => {
      const takes = r.ad && r.ad.shape === 'side' ? 2 : r.width
      for (let k = 0; k < takes && q < chosen.length; k++) out.set(chosen[q++].id, ri)
    })
    return out
  }, [rows, chosen])
  const selRow = sel != null && rowOf.has(sel) ? rowOf.get(sel)! : null
  const row: MagRow | null = selRow != null ? rows[selRow] ?? null : null

  // Does the last test still show what would go out? Same fingerprint as the
  // send function's (subject, preview text, HTML).
  const [hash, setHash] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    void issueHash(subject.trim(), pre, html).then((h) => { if (live) setHash(h) })
    return () => { live = false }
  }, [subject, pre, html])
  const gate = sendGate({
    family, list, subscribers: listSize, example, storyCount: chosen.length, subject, note: config.note, noteOn: sendConfig.noteOn,
    test: { to: draft?.test_sent_at ? (draft.test_sent_to ?? null) : null, current: !!draft?.test_hash && draft.test_hash === hash },
  })
  const issueDraft = () => ({ family, subject: subject.trim(), preheader: pre, config: sendConfig, html, plain_text: renderText(renderIn), design_id: designId })

  async function addPhoto(storyId: string, f: File | undefined) {
    if (!f || !clientId) return
    setPhotoBusy(storyId)
    try {
      const url = await uploadNewsletterImage(clientId, await shrinkPhoto(f), 'photo')
      setConfig((c) => ({ ...c, photos: { ...c.photos, [storyId]: url } }))
      toast.show('Photo added. Use your own photos, or ones you have the rights to.')
    } catch (e) { toast.show((e as Error).message) } finally { setPhotoBusy(null) }
  }
  function dropPhoto(storyId: string) {
    const next = { ...config.photos }
    delete next[storyId]
    setConfig({ ...config, photos: next })
    toast.show('Photo removed. This story goes out as just the article.')
  }

  function setRow(i: number, patch: Partial<MagRow>) {
    const next = [...config.magazine.rows]
    while (next.length <= i) next.push({ photo: 'over', width: 1, ad: null })
    next[i] = { ...next[i], ...patch }
    setConfig({ ...config, magazine: { rows: next } })
  }

  function addFromQueue() {
    if (!avail.length || chosen.length >= max) return
    setConfig({ ...config, storyIds: [...config.storyIds, avail[0].id] })
  }
  function removeStory(id: string) {
    setConfig({ ...config, storyIds: config.storyIds.filter((x) => x !== id) })
    if (sel === id) setSel(null)
  }
  function moveStory(id: string, d: -1 | 1) {
    const i = config.storyIds.indexOf(id)
    const j = i + d
    if (j < 0 || j >= config.storyIds.length) return
    const next = [...config.storyIds]; [next[i], next[j]] = [next[j], next[i]]
    setConfig({ ...config, storyIds: next })
  }
  function dropOn(target: string) {
    const from = dragId.current
    dragId.current = null
    if (!from || from === target) return
    const next = config.storyIds.filter((x) => x !== from)
    next.splice(next.indexOf(target), 0, from)
    setConfig({ ...config, storyIds: next })
  }
  function addOwn() {
    const t = compose?.title.trim()
    if (!t) return
    const id = `own-${Date.now().toString(36)}`
    setConfig({ ...config, own: [...(config.own ?? []), { id, headline: noDashes(t), body: noDashes(compose!.body.trim()) ?? '' }], storyIds: [id, ...config.storyIds] })
    setCompose(null)
    toast.show('Added at the top of your issue. Drag to move it anywhere.')
  }

  async function onSave() {
    const nm = name.trim() || `${chosen.length} stories, ${famName(family)}`
    try {
      const known = designId && designs?.some((d) => d.id === designId) ? designId : undefined
      const saved = await saveDesign.mutateAsync({ id: known, name: nm, family, config: sendConfig, brand_kit_id: brandId, updated_at: new Date().toISOString() } as Partial<Design>)
      setDesignId(saved.id!); setName(nm)
      await syncSlots.mutateAsync({
        designId: saved.id!,
        slots: lockedSlots(family, config, chosen.length).map((s) => ({ ...s, ...(config.rates?.[s.key] != null ? { rate_cents: Math.round(config.rates[s.key] * 100) } : {}) })),
      })
      toast.show(`Saved “${nm}”. It will still be here after a reload.`)
    } catch (e) { toast.show((e as Error).message) }
  }
  function onDelete() {
    const d = designs?.find((x) => x.id === designId)
    if (!d || !window.confirm(`Delete ${d.name}?`)) return
    delDesign.mutate(d.id, { onSuccess: () => { setDesignId(null); toast.show(`Deleted “${d.name}”.`) } })
  }

  async function onTest() {
    setSendMsg(null)
    try {
      const r = await testSend.mutateAsync({ id: draftId, issue: issueDraft() })
      setDraftId(r.issue.id)
      setSendMsg({ ok: `Test sent to ${r.to}.` })
    } catch (e) { setSendMsg({ err: (e as Error).message }) }
  }

  async function onSend() {
    setSendMsg(null)
    if (!gate.ready) { setSendMsg({ err: 'Finish the checklist first: everything with a circle still needs doing.' }); return }
    try {
      await send.mutateAsync({ id: draftId, issue: issueDraft(), onProgress: (n, t) => setProgress(`Sent to ${n.toLocaleString()} of ${t.toLocaleString()} so far…`) })
      setModal(null)
      setDraftId(null)
      setSubject('')
      toast.show(`Issue ${issueNo} is on its way${listSize ? ` to ${listSize.toLocaleString()} subscribers` : ''}.`)
    } catch (e) { setSendMsg({ err: (e as Error).message }) } finally { setProgress(null) }
  }

  const meta = config.meta ?? {}
  const setMeta = (patch: NonNullable<NewsletterConfig['meta']>) => setConfig({ ...config, meta: { ...meta, ...patch } })
  const metaOn = meta.on !== false
  const issueHint = config.issueAuto !== false ? (sent ? `Issue ${sent + 1} follows issue ${sent}, your last send.` : 'Issue 1, your first send.') : 'Overrides the running count.'
  const dateHint = config.dateAuto !== false ? 'Prints the day it sends.' : `Prints ${sendConfig.dateLabel}.`
  const saveNote = family === 'letter'
    ? 'Saves the story count and your note settings for The Letter.'
    : `Saves the story count, row layouts, locked ad space and rates for ${famName(family)}. Saved designs show up in the Sponsor desk too.`

  // Sponsors: the spots this layout locks, and who holds each today.
  const locked = lockedSlots(family, config, chosen.length)
  const spotRows = family === 'bulletin'
    ? Array.from({ length: config.bulletin.slots }, (_, i) => ({ key: `bulletin-${i}`, name: BULLETIN_SPOTS[i] ?? `Section ${i + 1} sponsor`, shape: 'banner' as AdShape, locked: config.bulletin.locked.includes(i), i }))
    : locked.map((s) => ({ key: s.key, name: s.label, shape: s.shape, locked: true, i: -1 }))
  const holder = (key: string) => fills[key]?.sponsor ?? null
  const filled = spotRows.filter((s) => s.locked && holder(s.key)).length
  const sponsorIntro = family === 'bulletin'
    ? 'Fixed banner space. One 600×150 banner below each section. Creatives come from your gallery.'
    : family === 'magazine'
      ? (locked.length
        ? 'Every spot here is a position you locked in the layout. Assign a sponsor, or run it as an open ad spot.'
        : 'No ad space yet. Select a row and lock a banner, left or right ad to create a spot.')
      : 'The Letter runs no ads. It reads as a note from you. Nothing sold inside it.'
  const deskLink = `/app/newsletter/sponsors${designId ? `?design=${designId}` : ''}`

  // Magazine: the row a selected story sits in.
  const needSel = (msg: string) => { if (selRow == null) { toast.show(msg); return true } return false }
  const partner = selRow != null && row ? (() => {
    const ids = [...rowOf.entries()].filter(([, r]) => r === selRow).map(([id]) => id)
    const last = chosen.findIndex((s) => s.id === ids[ids.length - 1])
    return row.width === 2 || (last >= 0 && last < chosen.length - 1)
  })() : false
  const lockAd = (kind: 'banner' | 'left' | 'right') => {
    if (needSel('Click a story first. The ad locks to that position.')) return
    setRow(selRow!, { ad: kind === 'banner' ? { shape: 'banner' } : { shape: 'side', side: kind } })
  }
  const adKind = row?.ad ? (row.ad.shape === 'side' ? row.ad.side : row.ad.shape) : null
  const rowKey = selRow != null ? `mag-${selRow}` : null
  const rowSlot = rowKey ? (slots ?? []).find((s) => s.design_id === designId && s.slot_key === rowKey) : undefined
  const rate = rowKey ? (config.rates?.[rowKey] ?? (rowSlot ? rowSlot.rate_cents / 100 : '')) : ''
  const selStory = sel ? pool.find((s) => s.id === sel) : null
  const rowLayoutHint = selStory
    ? `Applies to "${selStory.headline.slice(0, 42)}…" only.`
    : 'Click a story above to select it, then pick how that one row looks.'

  // The row tabs beside the preview: one per row (a magazine row may hold two stories).
  const tabs = family === 'magazine'
    ? rows.map((r, ri) => {
      const first = chosen.find((s) => rowOf.get(s.id) === ri)
      return first ? { id: first.id, label: `Row ${ri + 1}${r.ad ? ' · ad' : ''}`, title: r.ad ? `${r.ad.shape === 'banner' ? 'Banner ad + ' : `Locked ${r.ad.shape === 'side' ? r.ad.side : r.ad.shape} ad + `}${first.headline}` : first.headline } : null
    }).filter(Boolean) as { id: string; label: string; title: string }[]
    : chosen.map((s, i) => ({ id: s.id, label: `Row ${i + 1}`, title: s.headline }))
  const canAdd = avail.length > 0 && chosen.length < max

  const sections = family === 'bulletin' ? bulletinSections(chosen, config.bulletin.titles) : null
  const storyRow = (s: EmailStory) => {
    const i = config.storyIds.indexOf(s.id)
    return (
      <div key={s.id} className={`nl-srow${sel === s.id ? ' is-sel' : ''}`}
        onClick={() => setSel(sel === s.id ? null : s.id)}
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); dropOn(s.id) }}>
        <button type="button" className="nl-srow__grip" draggable title="Drag to reorder, or focus and press ↑ / ↓" aria-label="Reorder story"
          onDragStart={(e) => { dragId.current = s.id; e.dataTransfer.effectAllowed = 'move' }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); moveStory(s.id, e.key === 'ArrowUp' ? -1 : 1) } }}>{GRIP}</button>
        <span className="nl-srow__n">{i + 1}</span>
        <span className="nl-srow__t">{s.headline}</span>
        {/* Oct 29 (Brian): a photo is optional on every story; without one it goes out as just the article */}
        {config.photos?.[s.id] ? (
          <span className="nl-srow__pic" onClick={(e) => e.stopPropagation()}>
            <label title="Change the photo"><img src={config.photos[s.id]} alt="" /><input type="file" accept="image/*" hidden onChange={(e) => void addPhoto(s.id, e.target.files?.[0])} /></label>
            <button type="button" title="Remove the photo (just the article)" aria-label="Remove the photo" onClick={() => dropPhoto(s.id)}>✕</button>
          </span>
        ) : (
          <label className="nl-srow__add" title="Add your own photo to this story (optional)" onClick={(e) => e.stopPropagation()}>
            {photoBusy === s.id ? 'Adding' : '＋ Photo'}<input type="file" accept="image/*" hidden onChange={(e) => void addPhoto(s.id, e.target.files?.[0])} />
          </label>
        )}
        <button type="button" className="nl-srow__ic" title="Link to the original" aria-label="Link to the original"
          onClick={(e) => { e.stopPropagation(); setLinkOpen(linkOpen === s.id ? null : s.id); setLinkDraft(config.links?.[s.id] ?? s.source_url ?? '') }}>{LINK}</button>
        <button type="button" className="nl-srow__ic" title="Remove from issue" aria-label="Remove from issue" onClick={(e) => { e.stopPropagation(); removeStory(s.id) }}>×</button>
        {linkOpen === s.id && (
          <div className="nl-srow__link" onClick={(e) => e.stopPropagation()}>
            <input className="gg-input" value={linkDraft} onChange={(e) => setLinkDraft(e.target.value)} placeholder="Paste a link to the original: event page, website, or Facebook" />
            <button type="button" className="gg-btn gg-btn--sm" onClick={() => { setConfig({ ...config, links: { ...config.links, [s.id]: linkDraft.trim() } }); setLinkOpen(null) }}>Save</button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="nl-bwrap">
      <div className="nl-btop">
        <Link to="/app/newsletter" className="gg-btn gg-btn--sm">← Gallery</Link>
        <span className="nl-tabs" role="group" aria-label="Family">
          {FAMILIES.map((f) => (
            <button key={f} type="button" aria-pressed={f === family} onClick={() => navigate(`/app/newsletter/build/${f}${designId ? `?design=${designId}` : ''}`, { replace: true })}>{famName(f)}</button>
          ))}
        </span>
        <span className="nl-btop__end">
          <span className="nl-btop__draft">Draft · {dayShort(config.dateAuto === false && config.sendDate ? isoDate(config.sendDate) : new Date())}</span>
          {savedDraft && <span className="nl-btop__saved">Saved</span>}
          <button type="button" className="gg-btn gg-btn--sm" onClick={() => setModal('preview')}>Preview</button>
          <button type="button" className="gg-btn gg-btn--sm gg-btn--amber" onClick={() => { setSendMsg(null); setModal('send') }}>Send…</button>
        </span>
      </div>
      <div className="nl-builder">
        <aside className="nl-builder__panel">
          {example && (
            <p className="nl-example nl-example--b"><b>Example issue.</b> These stories are samples so you can see the layout. Tap ＋ Newsletter on a story in your feed and yours replace them.</p>
          )}

          <section className="nl-pc">
            <div className="nl-pc__h"><span>Your designs</span><i /><em>{designs?.length ?? 0} saved</em></div>
            <div className="nl-pc__row">
              <select className="gg-select" aria-label="Saved layouts" value={designId ?? ''} onChange={(e) => {
                const d = designs?.find((x) => x.id === e.target.value)
                if (d) applyDesign(d, true); else setDesignId(null)
              }}>
                <option value="">Saved designs…</option>
                {designs?.map((d) => <option key={d.id} value={d.id}>{d.name} · {famName(d.family)}</option>)}
              </select>
              {designId && designs?.some((d) => d.id === designId) && <button type="button" className="gg-btn gg-btn--sm gg-btn--quiet" onClick={onDelete}>Delete</button>}
            </div>
            <div className="nl-pc__row">
              <input className="gg-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this design, e.g. 6 stories, banner up top" />
              <button type="button" className="gg-btn gg-btn--sm gg-btn--amber" onClick={() => void onSave()} disabled={saveDesign.isPending}>{saveDesign.isPending ? 'Saving' : 'Save layout'}</button>
            </div>
            <p className="nl-pc__help">{saveNote}</p>
          </section>

          <section className="nl-pc">
            <div className="nl-pc__h"><span>Banner details</span><i /><Pill on={metaOn} onClick={() => setMeta({ on: !metaOn })}>{metaOn ? 'On' : 'Off'}</Pill></div>
            {metaOn ? (
              <>
                <div className="nl-pc__kv">
                  <span>Issue</span>
                  <span className="nl-pills">
                    <Pill on={config.issueAuto !== false} onClick={() => setConfig({ ...config, issueAuto: true })}>Auto</Pill>
                    <Pill on={config.issueAuto === false} onClick={() => setConfig({ ...config, issueAuto: false, issueNo: issueNo })}>Set it</Pill>
                  </span>
                  {config.issueAuto === false && <input className="gg-input nl-num" inputMode="numeric" aria-label="Issue number" value={String(config.issueNo ?? '')} onChange={(e) => setConfig({ ...config, issueNo: Number(e.target.value.replace(/[^0-9]/g, '')) || undefined })} />}
                  <small>{issueHint}</small>
                </div>
                <div className="nl-pc__kv">
                  <span>Date</span>
                  <span className="nl-pills">
                    <Pill on={config.dateAuto !== false} onClick={() => setConfig({ ...config, dateAuto: true })}>Auto</Pill>
                    <Pill on={config.dateAuto === false} onClick={() => setConfig({ ...config, dateAuto: false, sendDate: config.sendDate || today() })}>Scheduled</Pill>
                  </span>
                  {config.dateAuto === false && <input className="gg-input" type="date" aria-label="Send date" value={config.sendDate ?? ''} onChange={(e) => setConfig({ ...config, sendDate: e.target.value })} />}
                  <small>{dateHint}</small>
                </div>
                <div className="nl-pc__kv">
                  <span>Color</span>
                  <span className="nl-pills">
                    <Pill on={meta.color === 'black'} onClick={() => setMeta({ color: 'black' })}>Black</Pill>
                    <Pill on={meta.color === 'white'} onClick={() => setMeta({ color: 'white' })}>White</Pill>
                    <Pill on={meta.color === 'custom'} onClick={() => setMeta({ color: 'custom', custom: meta.custom ?? '#a06530' })}>Custom</Pill>
                  </span>
                  {meta.color === 'custom' && <input type="color" aria-label="Banner text color" value={meta.custom ?? '#a06530'} onChange={(e) => setMeta({ color: 'custom', custom: e.target.value })} />}
                </div>
                <div className="nl-pc__kv">
                  <span>Text size</span>
                  <input type="range" min={9} max={15} step={0.5} value={meta.size ?? 10} onChange={(e) => setMeta({ size: Number(e.target.value) })} />
                  <code>{meta.size ?? 10}px</code>
                </div>
              </>
            ) : <p className="nl-pc__help">The masthead prints no issue number or date.</p>}
          </section>

          {family !== 'letter' && (
            <section className="nl-pc">
              <div className="nl-pc__h"><span>Top of issue</span></div>
              <div className="nl-pc__kv">
                <span>Note</span>
                <span className="nl-pills">
                  <Pill on={config.noteOn} onClick={() => setConfig({ ...config, noteOn: !config.noteOn })}>A note from you</Pill>
                  {config.noteOn && <Pill on={config.noteItalic !== false} onClick={() => setConfig({ ...config, noteItalic: config.noteItalic === false })}>Italic</Pill>}
                </span>
              </div>
              {config.noteOn && <textarea className="gg-textarea" value={config.note} onChange={(e) => setConfig({ ...config, note: e.target.value })} />}
              <div className="nl-pc__kv">
                <span>Contents</span>
                <Pill on={config.toc !== false} onClick={() => setConfig({ ...config, toc: config.toc === false })}>In this issue list</Pill>
                <small>Headlines only. Jump links don't work in Gmail.</small>
              </div>
            </section>
          )}

          {family === 'letter' && (
            <section className="nl-pc">
              <div className="nl-pc__h"><span>Your note</span></div>
              <textarea className="gg-textarea" value={config.note} onChange={(e) => setConfig({ ...config, note: e.target.value })} />
              <p className="nl-pc__help">This is the heart of The Letter. It's what makes it yours.</p>
              <div><Pill on={!!config.bigLead} onClick={() => setConfig({ ...config, bigLead: !config.bigLead })}>Big lead story</Pill></div>
            </section>
          )}

          {family === 'magazine' && (
            <section className="nl-pc">
              <div className="nl-pc__h"><span>Layout</span></div>
              <p className="nl-pc__help">The Magazine is laid out row by row. Select a row, then set its photo position, width, and any locked ad space below.</p>
              <div className="nl-pc__sub">
                <div className="nl-pc__kv"><span>Customize</span><b className="nl-pc__rn">{selRow != null ? 'Row selected' : 'No row selected'}</b></div>
                <div className="nl-pc__kv">
                  <span>Width</span>
                  <span className="nl-pills">
                    <Pill on={row?.width === 1 && adKind !== 'left' && adKind !== 'right'} onClick={() => { if (!needSel('Click a story in the list first, then set its width.')) setRow(selRow!, { width: 1 }) }}>1 story</Pill>
                    <Pill on={row?.width === 2} disabled={selRow != null && (!partner || row?.ad?.shape === 'side')} onClick={() => { if (!needSel('Click a story in the list first, then set its width.')) setRow(selRow!, { width: 2 }) }}>2 stories</Pill>
                  </span>
                </div>
                <div className="nl-pc__kv">
                  <span>Ad space</span>
                  <span className="nl-pills">
                    <Pill on={adKind === 'banner'} onClick={() => lockAd('banner')}>Lock banner</Pill>
                    <Pill on={adKind === 'left'} onClick={() => lockAd('left')}>Lock left ad</Pill>
                    <Pill on={adKind === 'right'} onClick={() => lockAd('right')}>Lock right ad</Pill>
                    {row?.ad && <button type="button" className="nl-pill nl-pill--warn" onClick={() => setRow(selRow!, { ad: null })}>Unlock</button>}
                  </span>
                </div>
                {row?.ad && rowKey && (
                  <div className="nl-pc__kv">
                    <span>Rate</span>
                    <span className="nl-rate">$<input inputMode="numeric" aria-label="Ad rate" value={String(rate)} onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, '')
                      setConfig({ ...config, rates: { ...config.rates, [rowKey]: Number(v || 0) } })
                    }} />/issue · shows in the media kit</span>
                  </div>
                )}
                <div className="nl-pc__kv">
                  <span>Section layout</span>
                  <span className="nl-pills">
                    {(['over', 'left', 'right'] as const).map((p) => (
                      <Pill key={p} on={selRow != null && row?.photo === p} disabled={row?.width === 2 || row?.ad?.shape === 'side'}
                        onClick={() => { if (!needSel('Click a story in the list first, then pick its layout.')) setRow(selRow!, { photo: p }) }}>
                        {p === 'over' ? 'Photo over story' : p === 'left' ? 'Photo left' : 'Photo right'}
                      </Pill>
                    ))}
                    {row && (row.photo !== 'over' || row.width !== 1) && (
                      <button type="button" className="nl-pill" onClick={() => setRow(selRow!, { photo: 'over', width: 1 })}>Reset</button>
                    )}
                  </span>
                </div>
                <p className="nl-pc__help">{rowLayoutHint}</p>
                <div className="nl-pc__kv">
                  <span>Density</span>
                  <span className="nl-pills">
                    <Pill on={config.density === 'cozy'} onClick={() => setConfig({ ...config, density: 'cozy' })}>Comfortable</Pill>
                    <Pill on={config.density === 'compact'} onClick={() => setConfig({ ...config, density: 'compact' })}>Compact</Pill>
                  </span>
                  <small>Set this last. It tightens every row at once.</small>
                </div>
              </div>
              {/* Ours: no story pictures go in the newsletter, only the creator's own (Brian, Oct 21). */}
              <p className="nl-pc__help">No story pictures go in the newsletter. Add your own photo to a story in the list if you want one.</p>
            </section>
          )}

          {family === 'bulletin' && (
            <section className="nl-pc">
              <div className="nl-pc__h"><span>Quick layout</span></div>
              <div className="nl-pc__kv">
                <span>Stories</span>
                <span className="nl-pills">
                  {([['list', 'Headline list'], ['pairs', '2-up cards'], ['stack', '1 per row'], ['lead', 'Section lead'], ['alt', 'Alternating']] as const).map(([k, l]) => (
                    <Pill key={k} on={config.bulletin.preset === k} onClick={() => setConfig({ ...config, bulletin: { ...config.bulletin, preset: k } })}>{l}</Pill>
                  ))}
                </span>
              </div>
              <p className="nl-pc__help">Applies inside every section. Sponsor sections keep their position.</p>
              {/* Ours: sections follow the stories' topics, and each can be renamed. */}
              {sections && sections.length > 0 && (
                <div className="nl-pc__sub">
                  {sections.map((sec) => (
                    <label key={sec.key} className="nl-sectitle">
                      <em>{sec.key}</em>
                      <input className="gg-input" value={config.bulletin.titles?.[sec.key] ?? ''} placeholder={sec.key} maxLength={40}
                        onChange={(e) => setConfig({ ...config, bulletin: { ...config.bulletin, titles: { ...config.bulletin.titles, [sec.key]: e.target.value } } })}
                        aria-label={`Section title for ${sec.key}`} />
                    </label>
                  ))}
                </div>
              )}
            </section>
          )}

          <section className="nl-pc">
            <div className="nl-pc__h">
              <span>{family === 'bulletin' ? 'Stories · in sections' : family === 'letter' ? 'Stories · 3–6' : 'Stories · 4–12'}</span><i />
              <em>drag ≡ · or ↑↓ keys</em>
            </div>
            <div className="nl-slist">
              {sections
                ? sections.map((sec) => (
                  <div key={sec.key}>
                    <div className="nl-slist__sec">{sec.title}</div>
                    {sec.stories.map(storyRow)}
                  </div>
                ))
                : chosen.map(storyRow)}
            </div>
            <div className="nl-pc__row nl-pc__row--wrap">
              {canAdd && <button type="button" className="gg-btn gg-btn--sm" onClick={addFromQueue}>+ Add from queue ({avail.length})</button>}
              <button type="button" className="gg-btn gg-btn--sm" onClick={() => setCompose(compose ? null : { title: '', body: '' })}>Write your own</button>
              <small>
                {family === 'bulletin' ? 'Sections follow your stories’ topics.'
                  : family === 'letter' ? 'Photos are optional: ＋ Photo adds your own to a story; without one it goes out as just the article.'
                    : 'Photos are optional: ＋ Photo on a story adds your own; without one it goes out as just the article.'}
              </small>
            </div>
            {compose && (
              <div className="nl-compose">
                <input className="gg-input" value={compose.title} onChange={(e) => setCompose({ ...compose, title: e.target.value })} placeholder="Headline, e.g. What I heard at Saturday's open houses" />
                <textarea className="gg-textarea" rows={4} value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} placeholder="Write it here. A few sentences is plenty." />
                <div className="nl-pc__row nl-pc__row--wrap">
                  <button type="button" className="gg-btn gg-btn--sm gg-btn--amber" onClick={addOwn} disabled={!compose.title.trim()}>Add to issue</button>
                  <button type="button" className="gg-btn gg-btn--sm" onClick={() => setCompose(null)}>Cancel</button>
                  <small>Lands at the top. Drag to move it.</small>
                </div>
              </div>
            )}
            {chosen.length < min && <p className="nl-pc__help">{famName(family)} needs at least {min} stories.</p>}
          </section>

          <section className="nl-pc">
            <div className="nl-pc__h"><span>Brand</span></div>
            <div className="nl-pills">
              {(brands?.length ? brands : [{ ...DEFAULT_BRAND, id: '', is_default: true }]).map((k) => (
                <Pill key={k.id || 'house'} on={(brandId ?? brand.id ?? '') === (k.id ?? '') || (!brandId && k.is_default)} onClick={() => setBrandId(k.id || null)}>{k.name}</Pill>
              ))}
              <i className="nl-grow" />
              <Link className="gg-btn gg-btn--sm" to="/app/newsletter/brand">Brand studio →</Link>
            </div>
            <p className="nl-pc__help">Kits apply across all three newsletter styles.</p>
          </section>

          <section className="nl-pc">
            <div className="nl-pc__h"><span>Sponsors</span><i /><em>{filled} of {spotRows.filter((s) => s.locked).length} filled · direct-sold</em></div>
            <p className="nl-pc__help">{sponsorIntro}</p>
            {family === 'bulletin' && (
              <div className="nl-pc__kv">
                <span>Sponsor sections</span>
                <span className="nl-step">
                  <button type="button" aria-label="Fewer sections" onClick={() => { const n = Math.max(2, config.bulletin.slots - 1); setConfig({ ...config, bulletin: { ...config.bulletin, slots: n, locked: config.bulletin.locked.filter((i) => i < n) } }) }}>−</button>
                  <b>{config.bulletin.slots}</b>
                  <button type="button" aria-label="More sections" onClick={() => setConfig({ ...config, bulletin: { ...config.bulletin, slots: Math.min(6, config.bulletin.slots + 1) } })}>+</button>
                </span>
                <Pill on={config.bulletin.slots === 6} onClick={() => setConfig({ ...config, bulletin: { ...config.bulletin, slots: config.bulletin.slots === 6 ? 2 : 6, locked: config.bulletin.slots === 6 ? config.bulletin.locked.filter((i) => i < 2) : config.bulletin.locked } })}>Max ad slots</Pill>
              </div>
            )}
            {spotRows.length > 0 && (
              <div className="nl-spots">
                {spotRows.map((s) => {
                  const who = s.locked ? holder(s.key) : null
                  const pl = spotPlace(s.key, s.shape)
                  return (
                    <div key={s.key} className={`nl-spot${who ? ' is-filled' : ''}`}>
                      <span className="nl-spot__av">{who ? initials(who) : '＋'}</span>
                      <div className="nl-spot__t">
                        <b>{who ?? s.name}</b>
                        <span>{pl.place} · {pl.dim}</span>
                        {family === 'bulletin' && (
                          <span className="nl-spot__promo">
                            <Pill on={s.locked} onClick={() => setConfig({ ...config, bulletin: { ...config.bulletin, locked: s.locked ? config.bulletin.locked.filter((x) => x !== s.i) : [...config.bulletin.locked, s.i] } })}>{s.locked ? 'Locked in layout' : 'Lock this banner'}</Pill>
                            <small>Holds the banner position even when unsold.</small>
                          </span>
                        )}
                      </div>
                      {s.locked && <Link className="gg-btn gg-btn--sm" to={deskLink}>{who ? 'Replace' : 'Assign'}</Link>}
                    </div>
                  )
                })}
              </div>
            )}
            {family !== 'letter' && <div><Link className="gg-btn gg-btn--sm" to={deskLink}>Creative gallery →</Link></div>}
            <p className="nl-pc__help">Sponsors pay you directly. GoGrov takes no cut of sponsor revenue, and no email service we connect to ever will.</p>
          </section>
        </aside>

        <section className="nl-builder__preview" aria-label="Preview at send width">
          <div className="nl-rtabs">
            {tabs.map((t) => (
              <button key={t.id} type="button" className={`nl-rtab${sel === t.id ? ' is-on' : ''}${t.label.endsWith('· ad') ? ' is-ad' : ''}`} title={t.title} onClick={() => setSel(sel === t.id ? null : t.id)}>{t.label}</button>
            ))}
            <button type="button" className="nl-rtab nl-rtab--add" title={canAdd ? 'Add the next story from your queue' : 'Your queue is empty. Write your own story'}
              onClick={() => {
                if (canAdd) { addFromQueue(); toast.show('Added the next story from your queue.') }
                else { setCompose({ title: '', body: '' }); toast.show('Every story in your queue is already placed. Write your own below.') }
              }}>{canAdd ? '+ Add row' : '+ Write row'}</button>
          </div>
          <EmailFrame html={html} />
        </section>
      </div>

      {modal === 'preview' && <PreviewModal family={family} html={html} onClose={() => setModal(null)} />}
      {modal === 'asSent' && <AsSentModal html={html} from={fromName} sendsFrom={sendsFrom} replyTo={list?.reply_to ?? null} subject={subject.trim()} preheader={pre} onClose={() => setModal('send')} />}
      {modal === 'send' && (
        <Modal label={`Send issue ${issueNo}: ${famName(family)}`} onClose={() => setModal(null)}>
          <ModalHead title={`Send issue ${issueNo}: ${famName(family)}`} onClose={() => setModal(null)} />
          <div className="nl-send">
            <p className="gg-help">From <b>{fromName}</b> ({sendsFrom}){isEmail(list?.reply_to) ? `, replies to ${list!.reply_to}` : ''}, to the {listSize.toLocaleString()} people on Your list.</p>
            {sending && !sending.configured && <p className="gg-error">Newsletter sending isn’t set up yet. GoGrov’s email service needs its key and sending domain, which an admin adds in the admin portal.</p>}
            <label className="gg-field"><span>Subject line</span><input className="gg-input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={150} placeholder="What hits your subscribers' inboxes" /></label>
            {chosen.length > 0 && (
              <div className="nl-send__subjects">
                {chosen.slice(0, 3).map((st) => <button key={st.id} type="button" onClick={() => setSubject(st.headline)}>{st.headline}</button>)}
              </div>
            )}
            <label className="gg-field"><span>Preview text</span><input className="gg-input" value={preheader} onChange={(e) => setPreheader(e.target.value)} maxLength={150} placeholder="The line inboxes show after the subject" /></label>
            <div className="nl-send__steps">
              {gate.steps.map((st) => {
                const mark = st.info ? 'i' : st.done ? '✓' : ''
                const showFix = !!st.fix && (!st.done || st.fix === 'Send again')
                return (
                  <div key={st.key} className="nl-send__step">
                    <span className={`nl-send__dot${st.info ? ' is-info' : st.done ? ' is-done' : st.must ? ' is-must' : ''}`} aria-label={st.info ? 'Note' : st.done ? 'Done' : st.must ? 'Needed' : 'Optional'}>{mark}</span>
                    <span className="nl-send__text"><b>{st.label}</b><span>{st.note}</span></span>
                    {showFix && st.key === 'list' && <Link className="gg-btn gg-btn--sm" to={ROUTES.newsletterConnect}>{st.fix}</Link>}
                    {showFix && st.key === 'stories' && <Link className="gg-btn gg-btn--sm" to={ROUTES.feed}>{st.fix}</Link>}
                    {showFix && st.key === 'test' && (
                      <button className="gg-btn gg-btn--sm" disabled={!isEmail(list?.reply_to) || !subject.trim() || testSend.isPending} onClick={() => void onTest()}>
                        {testSend.isPending ? 'Sending' : st.fix}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
            {/* Oct 29 (Brian: "control the look … ready for Vigenti to hand off to Resend"): the email as inboxes get it */}
            <div><button type="button" className="gg-btn gg-btn--sm" onClick={() => setModal('asSent')}>See it as it will arrive</button></div>
            <p className="nl-send__line">{gate.line}</p>
            {progress && <p className="gg-help">{progress}</p>}
            {sendMsg?.ok && <p className="gg-ok">{sendMsg.ok}</p>}
            {sendMsg?.err && <p className="gg-error">{sendMsg.err}</p>}
            <div className="nl-send__foot">
              <span className="nl-send__warn">{gate.warn}</span>
              <button className="gg-btn" onClick={() => setModal(null)}>Cancel</button>
              <button className={`gg-btn gg-btn--amber${gate.ready ? '' : ' is-locked'}`} aria-disabled={!gate.ready} disabled={send.isPending} onClick={() => void onSend()}>
                {send.isPending ? 'Sending' : gate.label}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {toast.node}
    </div>
  )
}

type Kit = BrandKit & { id?: string; is_default?: boolean }

function BrandStudio() {
  const clientId = useEffectiveClientId()
  const client = useEffectiveClient()
  const navigate = useNavigate()
  const { data: kits } = useBrandKits()
  const { data: issues } = useIssues()
  const save = useNlSave<Partial<BrandKit & { id: string; is_default: boolean }>>('brand_kits', 'nl-brands')
  const del = useNlDelete('brand_kits', 'nl-brands')
  const { stories } = useIssueStories()
  const toast = useToast()
  const [editing, setEditing] = useState<Kit | null>(null)
  const [busy, setBusy] = useState(false)
  // Brian's studio previews the family you came from.
  const kept = useMemo(() => readDraft(clientId), [clientId])
  const family: Family = kept?.family ?? 'letter'

  useEffect(() => {
    if (!editing && kits) setEditing(kits.find((k) => k.is_default) ?? kits[0] ?? { ...DEFAULT_BRAND, is_default: true })
  }, [kits, editing])

  if (!editing) return <div className="gg-legacy"><span className="gg-spinner" /></div>
  const k = editing
  const sent = (issues ?? []).filter((i) => i.status === 'sent').length
  const cfg = kept?.config ? { ...DEFAULT_CONFIG, ...kept.config } : { ...DEFAULT_CONFIG, note: 'A line from you, in your own words.' }
  const pool = [...ownStories(cfg), ...stories]
  const list = cfg.storyIds.length ? cfg.storyIds.map((id) => pool.find((s) => s.id === id)).filter(Boolean) as EmailStory[] : stories.slice(0, 4)
  const html = renderEmail({ family, config: stamped({ ...cfg, noteOn: family === 'letter' ? true : cfg.noteOn }, sent), brand: k, stories: list.length ? list : stories.slice(0, 4), publication: client?.display_name ?? 'Your publication', place: client?.city_state ?? '', ads: {} })
  const custom = k.header_style === 'banner' && !!k.banner_url
  const o: BannerOpts = k.banner_opts ?? {}
  const setO = (patch: BannerOpts) => setEditing({ ...k, banner_opts: { ...o, ...patch } })

  async function upload(kind: 'logo' | 'banner', f: File | undefined) {
    if (!f || !clientId) return
    setBusy(true)
    try {
      const url = await uploadNewsletterImage(clientId, f, kind)
      setEditing({ ...k, [kind === 'logo' ? 'logo_url' : 'banner_url']: url, ...(kind === 'banner' ? { header_style: 'banner' as const } : {}) })
    } catch (e) { toast.show((e as Error).message) } finally { setBusy(false) }
  }

  async function onSave() {
    try {
      if (k.is_default && kits) {
        for (const other of kits.filter((x) => x.is_default && x.id !== k.id)) await save.mutateAsync({ id: other.id, is_default: false })
      }
      const row = await save.mutateAsync({ id: k.id, name: k.name, colors: k.colors, font_heading: k.font_heading, font_body: k.font_body, header_style: k.header_style, banner_url: k.banner_url ?? null, logo_url: k.logo_url ?? null, banner_opts: k.banner_opts ?? null, is_default: !!k.is_default })
      setEditing(row as Kit)
      toast.show(`Saved “${k.name}”.`)
    } catch (e) { toast.show((e as Error).message) }
  }

  return (
    <div className="nl-bwrap">
      <div className="nl-btop">
        <button type="button" className="gg-btn gg-btn--sm" onClick={() => { if (((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0) navigate(-1); else navigate('/app/newsletter') }}>← Back</button>
        <b className="nl-btop__h">Brand studio</b>
        <span className="nl-btop__sub">Kits apply across all three newsletter styles</span>
      </div>
      <div className="nl-builder">
        <aside className="nl-builder__panel">
          <section className="nl-pc">
            <div className="nl-pc__h"><span>Style kits</span></div>
            <div className="nl-pills">
              {kits?.map((x) => <Pill key={x.id} on={x.id === k.id} onClick={() => setEditing(x)}>{x.name}</Pill>)}
              {!k.id && <Pill on onClick={() => undefined}>{k.name}</Pill>}
              <button type="button" className="nl-pill" onClick={() => setEditing({ ...DEFAULT_BRAND, name: 'New kit', is_default: !kits?.length })}>+ New kit</button>
            </div>
            <div className="nl-pc__row">
              <input className="gg-input" aria-label="Kit name" value={k.name} onChange={(e) => setEditing({ ...k, name: e.target.value })} />
              <button type="button" className="gg-btn gg-btn--sm gg-btn--quiet" onClick={() => {
                if (!k.id) { setEditing(null); return }
                if ((kits?.length ?? 0) <= 1) { toast.show('Keep at least one kit.'); return }
                if (!window.confirm(`Delete ${k.name}?`)) return
                del.mutate(k.id, { onSuccess: () => toast.show(`Deleted "${k.name}".`) }); setEditing(null)
              }}>Delete</button>
            </div>
            <label className="nl-check"><input type="checkbox" checked={!!k.is_default} onChange={(e) => setEditing({ ...k, is_default: e.target.checked })} /> Use as my default kit</label>
          </section>

          <section className="nl-pc">
            <div className="nl-pc__h"><span>Colors</span></div>
            {([['accent', 'Accent'], ['ink', 'Headings']] as const).map(([c, l]) => (
              <div key={c} className="nl-pc__kv">
                <span>{l}</span>
                <input type="color" aria-label={l} value={k.colors[c]} onChange={(e) => setEditing({ ...k, colors: { ...k.colors, [c]: e.target.value } })} />
                <code>{k.colors[c]}</code>
              </div>
            ))}
          </section>

          <section className="nl-pc">
            <div className="nl-pc__h"><span>Fonts</span></div>
            <div className="nl-pc__kv"><span>Headings</span><select className="gg-select" value={k.font_heading} onChange={(e) => setEditing({ ...k, font_heading: e.target.value })}>{EMAIL_FONTS.map((f) => <option key={f}>{f}</option>)}</select></div>
            <div className="nl-pc__kv"><span>Body</span><select className="gg-select" value={k.font_body} onChange={(e) => setEditing({ ...k, font_body: e.target.value })}>{EMAIL_FONTS.map((f) => <option key={f}>{f}</option>)}</select></div>
            <p className="nl-pc__help">Email-safe fonts only. These render in Gmail, Outlook and Apple Mail, so the preview matches the inbox.</p>
          </section>

          <section className="nl-pc">
            <div className="nl-pc__h"><span>Header</span></div>
            <div className={`nl-pills${custom ? ' is-off' : ''}`}>
              <Pill on={!custom && k.header_style !== 'minimal'} disabled={custom} onClick={() => setEditing({ ...k, header_style: 'masthead' })}>Color</Pill>
              <Pill on={!custom && k.header_style === 'minimal'} disabled={custom} onClick={() => setEditing({ ...k, header_style: 'minimal' })}>Minimal</Pill>
            </div>
            {/* Oct 29 (Brian's log #008: "give color option besides cream"): the header's colour; dark ones turn its words light */}
            {!custom && k.header_style === 'masthead' && (() => {
              const cur = (k.colors.header || CREAM).toLowerCase()
              const named = HEADER_COLORS.find(([, h]) => h === cur)
              return (
                <div className="nl-swatches">
                  {HEADER_COLORS.map(([label, hex]) => (
                    <button key={hex} type="button" className={`nl-swatch${hex === cur ? ' is-on' : ''}`} title={label} aria-label={label} aria-pressed={hex === cur}
                      style={{ background: hex }} onClick={() => setEditing({ ...k, colors: { ...k.colors, header: hex } })} />
                  ))}
                  <input type="color" aria-label="Your own header colour" title="Your own colour" value={cur} onChange={(e) => setEditing({ ...k, colors: { ...k.colors, header: e.target.value } })} />
                  <span className="nl-swatches__nm">{named ? named[0] : 'Your colour'}{isDark(cur) ? ' · light words' : ''}</span>
                </div>
              )
            })()}
            {custom && <p className="nl-pc__help">Your own banner is in use. Remove it to go back to these.</p>}
            <div className="nl-pc__row nl-pc__row--wrap">
              <span className="nl-pc__lbl">Your banner</span>
              <label className="gg-btn gg-btn--sm">{busy ? 'Uploading' : 'Upload image'}<input type="file" accept="image/*" hidden onChange={(e) => void upload('banner', e.target.files?.[0])} /></label>
              {k.banner_url && <button type="button" className="gg-btn gg-btn--sm gg-btn--quiet" onClick={() => setEditing({ ...k, banner_url: null, header_style: 'masthead' })}>Remove</button>}
            </div>
            <p className="nl-pc__help">600px wide renders sharpest. Your own banner replaces the name and tagline block.</p>
            {custom && (
              <div className="nl-pc__sub">
                <div className="nl-pc__kv"><span>Overlay</span><Pill on={o.metaShow !== false} onClick={() => setO({ metaShow: o.metaShow === false })}>Issue &amp; date</Pill></div>
                {o.metaShow !== false && (
                  <>
                    <div className="nl-pc__kv"><span>Position</span><span className="nl-pills">
                      {(['top', 'middle', 'bottom'] as const).map((p) => <Pill key={p} on={(o.metaPos ?? 'bottom') === p} onClick={() => setO({ metaPos: p })}>{p[0].toUpperCase() + p.slice(1)}</Pill>)}
                    </span></div>
                    <div className="nl-pc__kv"><span>Align</span><span className="nl-pills">
                      {(['left', 'center', 'right'] as const).map((p) => <Pill key={p} on={(o.metaAlign ?? 'center') === p} onClick={() => setO({ metaAlign: p })}>{p[0].toUpperCase() + p.slice(1)}</Pill>)}
                    </span></div>
                    <div className="nl-pc__kv"><span>Backing</span><span className="nl-pills">
                      <Pill on={(o.metaInk ?? 'light') === 'light'} onClick={() => setO({ metaInk: 'light' })}>Dark scrim</Pill>
                      <Pill on={o.metaInk === 'dark'} onClick={() => setO({ metaInk: 'dark' })}>Cream plate</Pill>
                    </span></div>
                  </>
                )}
                <div className="nl-pc__kv"><span>Height</span><input type="range" min={110} max={300} step={10} value={o.height ?? 150} onChange={(e) => setO({ height: Number(e.target.value) })} /><code>{o.height ?? 150}px</code></div>
              </div>
            )}
            {/* Ours: the logo over the masthead (the gallery's "colors, fonts, logo and header"). */}
            <div className="nl-pc__row nl-pc__row--wrap">
              <span className="nl-pc__lbl">Logo</span>
              <label className="gg-btn gg-btn--sm">Upload image<input type="file" accept="image/*" hidden onChange={(e) => void upload('logo', e.target.files?.[0])} /></label>
              {k.logo_url && <button type="button" className="gg-btn gg-btn--sm gg-btn--quiet" onClick={() => setEditing({ ...k, logo_url: null })}>Remove</button>}
            </div>
          </section>

          <div className="nl-builder__actions">
            <button className="gg-btn gg-btn--amber" disabled={save.isPending || busy} onClick={() => void onSave()}>{save.isPending ? 'Saving' : 'Save kit'}</button>
          </div>
        </aside>
        <section className="nl-builder__preview"><EmailFrame html={html} /></section>
      </div>
      {toast.node}
    </div>
  )
}

interface Reserve {
  slot: AdSlot; starts: string; ends: string; sponsorId: string; newName: string; endAction: Booking['end_action']; rate: string
  creative: string | null; crop: Booking['creative_crop']; warnings: string[]; holder: string | null
}
interface Upload { id: string; file: File; name: string; w: number; h: number; fit: string; warn: string }

function SponsorDesk() {
  const clientId = useEffectiveClientId()
  const client = useEffectiveClient()
  const [params] = useSearchParams()
  const { data: designs } = useDesigns()
  const { data: slots } = useAdSlots()
  const { data: bookings } = useBookings()
  const { data: sponsors } = useSponsors()
  const { data: brands } = useBrandKits()
  const { data: issues } = useIssues()
  const { data: list } = useYourList()
  const { data: stats } = useListStats()
  const listSize = stats?.subscribed ?? 0
  const { stories } = useIssueStories()
  const saveSlot = useNlSave<Partial<AdSlot>>('ad_slots', 'nl-slots')
  const saveSponsor = useNlSave<Partial<Sponsor>>('sponsors', 'nl-sponsors')
  const saveBooking = useNlSave<Partial<Booking>>('ad_bookings', 'nl-bookings')
  const delBooking = useNlDelete('ad_bookings', 'nl-bookings')
  const kit = useCreateMediaKit()
  const toast = useToast()
  const [dropOn, setDropOn] = useState<string | null>(null)
  const [deskFam, setDeskFam] = useState<'magazine' | 'bulletin'>('magazine')
  const [designId, setDesignId] = useState<string | null>(params.get('design'))
  const [newSponsor, setNewSponsor] = useState('')
  const [reserve, setReserve] = useState<Reserve | null>(null)
  const [picker, setPicker] = useState(false)
  const [spotSel, setSpotSel] = useState<string | null>(null)
  const [uploads, setUploads] = useState<Upload[]>([])
  const [kitLink, setKitLink] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const withSlots = (designs ?? []).filter((d) => d.family !== 'letter' && (slots ?? []).some((s) => s.design_id === d.id))
  const picked = withSlots.find((d) => d.id === designId)
  useEffect(() => { if (picked && picked.family !== 'letter') setDeskFam(picked.family) }, [picked?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const ofFam = withSlots.filter((d) => d.family === deskFam)
  const design = picked && picked.family === deskFam ? picked : ofFam[0]

  const designSlots = (slots ?? []).filter((s) => s.design_id === design?.id).sort((a, b) => a.slot_key.localeCompare(b.slot_key, undefined, { numeric: true }))
  const kitFills = useAdFills(slots, bookings, sponsors, design?.id ?? null, 'kit')
  const brand = brands?.find((b) => b.id === design?.brand_kit_id) ?? brands?.find((b) => b.is_default) ?? DEFAULT_BRAND
  const sent = (issues ?? []).filter((i) => i.status === 'sent').length
  const pool = design ? [...ownStories(design.config), ...stories] : stories
  const designStories = design ? design.config.storyIds.map((id) => pool.find((s) => s.id === id)).filter(Boolean) as EmailStory[] : []
  const kitHtml = design ? renderEmail({ family: design.family, config: stamped({ ...DEFAULT_CONFIG, ...design.config }, sent), brand, stories: designStories.length ? designStories : stories.slice(0, 6), publication: client?.display_name ?? '', place: client?.city_state ?? '', ads: kitFills, mode: 'kit' }) : ''
  const sponsorName = (id: string | null) => sponsors?.find((p) => p.id === id)?.name ?? 'Sponsor'
  const now = today()

  function lane(slot: AdSlot) {
    const list = (bookings ?? []).filter((b) => b.slot_id === slot.id && b.status !== 'cancelled' && b.status !== 'ended').sort((a, b) => a.starts_on.localeCompare(b.starts_on))
    const running = list.find((b) => b.starts_on <= now)
    const next = list.filter((b) => b.starts_on > now)
    const last = list[list.length - 1]
    // A booking not set to renew opens the spot the day after it ends.
    const opens = !last ? now : last.end_action === 'remind' ? null : addDays(last.ends_on, 1)
    return { list, running, next, last, opens }
  }

  const spots = designSlots.map((s, k) => ({ s, tag: String.fromCharCode(65 + k), ...spotPlace(s.slot_key, s.shape), name: s.label ?? SHAPES[s.shape].label, ...lane(s) }))
  const openCount = spots.filter((x) => !x.running).length
  const booked = spots.reduce((sum, x) => sum + (x.running ? x.running.rate_cents : 0), 0)
  const dims = [...new Set(spots.map((x) => x.dim))]
  const town = (client?.city_state ?? '').split(',')[0]?.trim() ?? ''
  const region = (client?.city_state ?? '').split(',').slice(1).join(',').trim()

  function openReserve(s: AdSlot, sponsorId?: string) {
    const l = lane(s)
    const starts = l.opens ?? addDays(l.last?.ends_on ?? now, 1)
    const base: Reserve = {
      slot: s, starts, ends: addDays(starts, 27), sponsorId: sponsorId ?? sponsors?.[0]?.id ?? '', newName: '', endAction: 'open', rate: String(s.rate_cents / 100),
      creative: null, crop: null, warnings: [], holder: l.last && starts > now ? sponsorName(l.last.sponsor_id) : null,
    }
    setReserve(base)
    return base
  }

  /** Drop an advertiser or a piece of artwork on a spot to start a booking. */
  function onDrop(e: React.DragEvent, s: AdSlot) {
    e.preventDefault()
    setDropOn(null)
    const sponsorId = e.dataTransfer.getData('text/x-gogrov-sponsor')
    const upId = e.dataTransfer.getData('text/x-gogrov-upload')
    const file = uploads.find((u) => u.id === upId)?.file ?? [...e.dataTransfer.files].find((f) => f.type.startsWith('image/'))
    const base = openReserve(s, sponsorId || undefined)
    if (file) void pickCreative(file, base)
  }

  async function ingest(files: FileList | null) {
    const list = [...(files ?? [])].filter((f) => f.type.startsWith('image/'))
    for (const file of list) {
      try {
        const d = await new Promise<{ w: number; h: number }>((res, rej) => {
          const im = new Image(); im.onload = () => res({ w: im.naturalWidth, h: im.naturalHeight }); im.onerror = () => rej(new Error('That image did not open.')); im.src = URL.createObjectURL(file)
        })
        const fit = creativeFit(d.w, d.h, 'banner')
        const best = SHAPES[fit.best]
        const bf = creativeFit(d.w, d.h, fit.best)
        const warn = [bf.pct >= 3 ? `Doesn’t match the ${best.label} shape. It will be cropped ${bf.pct}% to fit.` : '', d.w < best.w ? `Also under ${best.w}px wide, so it will look soft in the inbox.` : ''].filter(Boolean).join(' ')
        setUploads((u) => [...u, { id: `up${Date.now()}${Math.random().toString(36).slice(2, 6)}`, file, name: file.name.replace(/\.[a-z0-9]+$/i, ''), w: d.w, h: d.h, fit: `best fit ${best.label}`, warn }])
      } catch (e) { setErr((e as Error).message) }
    }
  }

  async function pickCreative(f: File | undefined, base = reserve) {
    if (!f || !base || !clientId) return
    setErr(null)
    try {
      const d = await new Promise<{ w: number; h: number }>((res, rej) => {
        const im = new Image(); im.onload = () => res({ w: im.naturalWidth, h: im.naturalHeight }); im.onerror = () => rej(new Error('That image did not open.')); im.src = URL.createObjectURL(f)
      })
      const fit = creativeFit(d.w, d.h, base.slot.shape)
      const url = await uploadNewsletterImage(clientId, f, 'ad')
      const tag = fit.best !== base.slot.shape ? [`Best fit for this artwork: ${SHAPES[fit.best].label}.`] : []
      setReserve((cur) => cur && cur.slot.id === base.slot.id ? { ...cur, creative: url, crop: fit.crop, warnings: [...fit.warnings, ...tag] } : cur)
    } catch (e) { setErr((e as Error).message) }
  }

  async function shareKit() {
    if (!design) return
    setErr(null)
    const snapshot: MediaKitSnapshot = {
      html: kitHtml,
      contact: list?.reply_to ?? null,
      audience: listSize ? `${listSize.toLocaleString()} subscribers` : null,
      spots: spots.map((x) => ({
        label: x.name, shape: x.s.shape, rate: x.s.rate_cents ? money(x.s.rate_cents) : 'Ask',
        status: x.running ? 'taken' : 'open', frees_on: x.running ? shortDate(addDays(x.running.ends_on, 1)) : null,
        // Never what another advertiser paid: only the open rate travels.
        next_rate: x.next.length ? null : x.opens ? (x.s.rate_cents ? money(x.s.rate_cents) : null) : null,
      })),
    }
    try {
      const token = await kit.mutateAsync(snapshot)
      const link = `${window.location.origin}/kit/${token}`
      setKitLink(link)
      try { await navigator.clipboard.writeText(link) } catch { /* shown below anyway */ }
      toast.show('Share link copied. Advertisers see prices, not your draft.')
    } catch (e) { setErr((e as Error).message) }
  }

  async function confirmReserve(r: Reserve) {
    setErr(null)
    try {
      let sponsorId = r.sponsorId
      let nm = sponsorName(sponsorId)
      if (!sponsorId) {
        if (!r.newName.trim()) { setErr('Choose an advertiser, or type a new one.'); return }
        const sp = await saveSponsor.mutateAsync({ name: r.newName.trim() })
        sponsorId = sp.id!; nm = r.newName.trim()
      }
      await saveBooking.mutateAsync({
        slot_id: r.slot.id, sponsor_id: sponsorId, starts_on: r.starts, ends_on: r.ends,
        end_action: r.endAction, rate_cents: Math.round(Number(r.rate || 0) * 100),
        creative_url: r.creative, creative_crop: r.crop,
      })
      setReserve(null)
      toast.show(r.holder && r.starts > now ? `${nm} is queued. Takes over ${shortDate(r.starts)}.` : `${nm} reserved through ${fullDate(r.ends)}.`)
    } catch (e) { setErr((e as Error).message) }
  }

  const reserveHint = (a: Booking['end_action']) => a === 'remind'
    ? 'The spot keeps running and you get a reminder a week before the end date.'
    : a === 'house_ad'
      ? 'On that date the spot switches itself to an “advertise here” card in the newsletter.'
      : 'The spot goes back to open on that date and shows its price again.'

  return (
    <div className="nl-bwrap">
      <div className="nl-btop">
        <Link to="/app/newsletter" className="gg-btn gg-btn--sm">← Gallery</Link>
        <b className="nl-btop__h">Sponsor desk</b>
        <span className="nl-tabs" role="group" aria-label="Family">
          {(['magazine', 'bulletin'] as const).map((f) => <button key={f} type="button" aria-pressed={deskFam === f} onClick={() => { setDeskFam(f); setDesignId(null); setSpotSel(null) }}>{famName(f)}</button>)}
        </span>
        <span className="nl-btop__end">
          <select className="gg-select" aria-label="Saved designs" value={design?.id ?? ''} onChange={(e) => setDesignId(e.target.value || null)}>
            <option value="">Saved designs…</option>
            {withSlots.map((d) => <option key={d.id} value={d.id}>{d.name} · {famName(d.family)}</option>)}
          </select>
          <button type="button" className="gg-btn gg-btn--sm" onClick={() => void shareKit()} disabled={!design || kit.isPending}>{kit.isPending ? 'Copying' : 'Copy share link'}</button>
          {kitLink && <a className="gg-btn gg-btn--sm" href={`${kitLink}?print=1`} target="_blank" rel="noreferrer">Download as PDF</a>}
        </span>
      </div>
      <div className="gg-legacy nl nl-desk">
        <div className="nl-desk__col">
          <section className="nl-pc">
            <div className="nl-pc__h"><span>Inventory &amp; rates</span><i /><em>{spots.length} spot{spots.length === 1 ? '' : 's'} · {openCount} open</em></div>
            <p className="nl-pc__help">Set your price per spot. What you type here is what the media kit shows.</p>
            {spots.length === 0 && <p className="nl-pc__empty">No ad space yet. In the builder, select a row and lock a banner, left or right ad to create a spot.</p>}
            {spots.map((x) => (
              <div key={x.s.id} className={`nl-dspot${spotSel === x.s.id ? ' is-sel' : ''}${dropOn === x.s.id ? ' is-drop' : ''}`}
                onClick={() => setSpotSel(x.s.id)}
                onDragOver={(e) => { e.preventDefault(); setDropOn(x.s.id) }} onDragLeave={() => setDropOn(null)} onDrop={(e) => onDrop(e, x.s)}>
                <div className="nl-dspot__row">
                  <span className="nl-dspot__tag">{x.tag}</span>
                  <span className="nl-dspot__t">
                    <b>{x.name}</b>
                    <span>{x.place} · {x.dim}</span>
                    <span className={x.running ? 'is-sold' : ''}>{x.running ? `Sold: ${sponsorName(x.running.sponsor_id)}` : 'Available'}</span>
                    {x.running && <button type="button" className="nl-link" onClick={(e) => { e.stopPropagation(); if (window.confirm('Cancel this booking?')) delBooking.mutate(x.running!.id) }}>Cancel</button>}
                  </span>
                  <span className="nl-rate">$<input type="number" min={0} step={5} defaultValue={x.s.rate_cents / 100} key={`${x.s.id}-${x.s.rate_cents}`} aria-label="Price per issue"
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => saveSlot.mutate({ id: x.s.id, rate_cents: Math.round(Number(e.target.value || 0) * 100) })} />/issue</span>
                </div>
                {x.next.map((b) => (
                  <div key={b.id} className="nl-dspot__row nl-dspot__row--next">
                    <span className="nl-dspot__tag">{x.tag}</span>
                    <span className="nl-dspot__t">
                      <b>{sponsorName(b.sponsor_id)}</b>
                      <span>Takes over {shortDate(b.starts_on)} · through {shortDate(b.ends_on)}</span>
                      <span className="nl-pills">
                        <Pill on={b.end_action === 'open'} onClick={() => { saveBooking.mutate({ id: b.id, end_action: 'open' }); toast.show('The spot reopens when that booking ends.') }}>Return to open</Pill>
                        <Pill on={b.end_action === 'remind'} onClick={() => { saveBooking.mutate({ id: b.id, end_action: 'remind' }); toast.show('That booking will roll over.') }}>Renew</Pill>
                      </span>
                    </span>
                    <button type="button" className="gg-btn gg-btn--sm gg-btn--quiet" onClick={(e) => { e.stopPropagation(); delBooking.mutate(b.id, { onSuccess: () => toast.show('Queued booking cancelled.') }) }}>Cancel</button>
                  </div>
                ))}
                {x.opens && (x.running || x.next.length > 0) && (
                  <button type="button" className="nl-dspot__row nl-dspot__row--open" onClick={(e) => { e.stopPropagation(); openReserve(x.s) }}>
                    <span className="nl-dspot__tag">{x.tag}</span>
                    <span className="nl-dspot__t"><b>Opens {shortDate(x.opens)}</b><span>Bookable now. Drag an advertiser here</span></span>
                  </button>
                )}
                {!x.running && x.next.length === 0 && (
                  <button type="button" className="nl-dspot__book" onClick={(e) => { e.stopPropagation(); openReserve(x.s) }}>Reserve this spot</button>
                )}
              </div>
            ))}
          </section>

          <section className="nl-pc">
            <div className="nl-pc__h"><span>Advertisers</span><i /><button type="button" className="gg-btn gg-btn--sm" onClick={() => setPicker(true)}>Creative gallery →</button></div>
            <label className="nl-drop" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); void ingest(e.dataTransfer.files) }}>
              <input type="file" accept="image/*" multiple hidden onChange={(e) => void ingest(e.target.files)} />
              <span>Drop an advertiser’s artwork here, or browse</span>
              <small>600×300 inline · 600×150 banner · 280×240 side</small>
            </label>
            {uploads.map((u) => (
              <div key={u.id} className="nl-adv" draggable title="Drag onto a spot to reserve it"
                onDragStart={(e) => { e.dataTransfer.setData('text/x-gogrov-upload', u.id); e.dataTransfer.effectAllowed = 'copy' }}>
                <span className="nl-adv__av">▦</span>
                <span className="nl-adv__t"><b>{u.name}</b><span>{u.w}×{u.h} · {u.fit}</span>{u.warn && <span className="nl-warn">{u.warn}</span>}</span>
                <button type="button" className="nl-srow__ic" aria-label="Remove" onClick={() => setUploads((l) => l.filter((x) => x.id !== u.id))}>×</button>
              </div>
            ))}
            {sponsors?.map((p) => {
              const mine = (bookings ?? []).filter((b) => b.sponsor_id === p.id && b.status !== 'cancelled' && b.status !== 'ended')
              const run = mine.find((b) => b.starts_on <= now)
              const q = mine.find((b) => b.starts_on > now)
              const link = p.contact || 'no link'
              const meta = run ? `${link} · through ${shortDate(run.ends_on)}` : q ? `${link} · queued from ${shortDate(q.starts_on)}` : `${link} · prospect`
              return (
                <div key={p.id} className="nl-adv" draggable title="Drag onto a spot to reserve it"
                  onDragStart={(e) => { e.dataTransfer.setData('text/x-gogrov-sponsor', p.id); e.dataTransfer.effectAllowed = 'copy' }}>
                  <span className="nl-adv__av">{initials(p.name)}</span>
                  <span className="nl-adv__t"><b>{p.name}</b><span>{meta}</span></span>
                  <b className="nl-adv__v">{run ? money(run.rate_cents) : 'None'}</b>
                </div>
              )
            })}
            <form className="nl-pc__row" onSubmit={(e) => { e.preventDefault(); if (newSponsor.trim()) saveSponsor.mutate({ name: newSponsor.trim() }, { onSuccess: () => setNewSponsor('') }) }}>
              <input className="gg-input" value={newSponsor} onChange={(e) => setNewSponsor(e.target.value)} placeholder="Add an advertiser" />
              <button className="gg-btn gg-btn--sm">Add</button>
            </form>
            <div className="nl-pc__kv nl-booked"><span>Booked this issue</span><b>${(booked / 100).toLocaleString()} / issue</b></div>
          </section>
          {err && <p className="gg-error">{err}</p>}
        </div>

        <div className="nl-desk__col">
          <div className="nl-mk">
            <div className="nl-mk__hd">
              <div className="nl-mk__eb">Media kit · {famName(deskFam)}</div>
              <div className="nl-mk__name">{client?.display_name ?? ''}</div>
              <div className="nl-mk__stats">
                <span><b>{listSize ? listSize.toLocaleString() : 'New'}</b><span>{listSize ? 'subscribers' : 'list, first issue'}</span></span>
                <span><b>{stats?.open_rate != null ? `${stats.open_rate}%` : 'Soon'}</b><span>{stats?.open_rate != null ? 'open rate' : 'open rate, after a send'}</span></span>
                <span><b>4</b><span>issues / month</span></span>
                <span><b>{town}</b><span>{region}</span></span>
              </div>
            </div>
            <div className="nl-mk__body">
              {spots.length === 0 ? (
                <div className="nl-mk__empty"><b>No ad space in this layout yet.</b><span>Lock a spot in the builder and it appears here, priced and ready to send.</span></div>
              ) : (
                <>
                  <div className="nl-mk__lbl">Where your ad runs</div>
                  <EmailFrame html={kitHtml} maxHeight={700} />
                  <div className="nl-mk__lbl">Rates</div>
                  {spots.map((x) => (
                    <button key={x.s.id} type="button" className="nl-mk__rate" onClick={() => setSpotSel(x.s.id)}>
                      <span><span className="nl-dspot__tag">{x.tag}</span><b>{x.name}</b>
                        <span>{x.running ? (x.next.length ? `Sold · booked through ${shortDate(x.next[x.next.length - 1].ends_on)}` : `Sold · open ${shortDate(addDays(x.running.ends_on, 1))}`) : 'Available'}</span>
                        {x.running && x.next[0] && <span>Then {sponsorName(x.next[0].sponsor_id)}</span>}
                      </span>
                      <span><b>{x.running ? 'Sold' : `$${(x.s.rate_cents / 100).toLocaleString()}`}</b><span>{x.dim} · per issue</span></span>
                    </button>
                  ))}
                  <p className="nl-mk__fine">Creative sizes are {dims.join(' px and ')} px, PNG or JPG. Artwork is due 3 days before send. You book directly with me. No marketplace, no middleman, and I only take sponsors I'd recommend to a neighbor.</p>
                </>
              )}
              <div className="nl-mk__cta"><span>Ready to book a spot?</span><span>{list?.reply_to ?? ''}</span></div>
            </div>
          </div>
          <p className="nl-pc__help">This is the page advertisers see. Prices update live as you edit them.</p>
        </div>
      </div>

      {picker && (
        <Modal label="Creative gallery" onClose={() => setPicker(false)}>
          <ModalHead title="Creative gallery" sub="Direct-sold advertisers · sizes standardized to 600×300 and 600×100" onClose={() => setPicker(false)} />
          <div className="nl-picker">
            {sponsors?.length ? sponsors.map((p) => {
              const used = (bookings ?? []).some((b) => b.sponsor_id === p.id && b.status !== 'cancelled' && b.status !== 'ended')
              return (
                <button key={p.id} type="button" className={`nl-adv${used ? ' is-used' : ''}`} onClick={() => {
                  const target = spots.find((x) => x.s.id === spotSel) ?? spots.find((x) => !x.running)
                  setPicker(false)
                  if (target) openReserve(target.s, p.id)
                }}>
                  <span className="nl-adv__av">{initials(p.name)}</span>
                  <span className="nl-adv__t"><b>{p.name}</b><span>{p.contact || 'no link'} · {used ? 'placed' : 'available'}</span></span>
                </button>
              )
            }) : <p className="nl-pc__help">Add an advertiser first.</p>}
          </div>
        </Modal>
      )}

      {reserve && (
        <Modal label="Reserve this spot" onClose={() => setReserve(null)}>
          <ModalHead title="Reserve this spot" onClose={() => setReserve(null)} />
          <div className="nl-res">
            <div className="nl-res__who">
              <span className="nl-adv__av" style={reserve.creative ? { backgroundImage: `url(${reserve.creative})`, color: 'transparent' } : undefined}>{initials(reserve.sponsorId ? sponsorName(reserve.sponsorId) : reserve.newName)}</span>
              <span className="nl-adv__t">
                <select className="gg-select" aria-label="Advertiser" value={reserve.sponsorId} onChange={(e) => setReserve({ ...reserve, sponsorId: e.target.value })}>
                  <option value="">New advertiser</option>{sponsors?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {!reserve.sponsorId && <input className="gg-input" value={reserve.newName} onChange={(e) => setReserve({ ...reserve, newName: e.target.value })} placeholder="Advertiser name" aria-label="Advertiser name" />}
                <span>{reserve.slot.slot_key.startsWith('bulletin-') ? 'Bulletin section banner' : 'Locked ad space'}</span>
              </span>
            </div>
            {reserve.holder && reserve.starts > now && (
              <p className="nl-pc__help">{reserve.holder} holds this spot through {shortDate(addDays(reserve.starts, -1))}. This booking is queued behind it and takes over automatically that day.</p>
            )}
            <div className="nl-pc__kv"><span>Starts</span><input className="gg-input" type="date" aria-label="Reservation start date" value={reserve.starts} onChange={(e) => setReserve({ ...reserve, starts: e.target.value })} /></div>
            <div className="nl-pc__kv"><span>Runs until</span><input className="gg-input" type="date" aria-label="Reservation end date" value={reserve.ends} onChange={(e) => setReserve({ ...reserve, ends: e.target.value })} /></div>
            <div className="nl-pc__kv"><span>Then</span><span className="nl-pills">
              <Pill on={reserve.endAction === 'open'} onClick={() => setReserve({ ...reserve, endAction: 'open' })}>Return to open</Pill>
              <Pill on={reserve.endAction === 'house_ad'} onClick={() => setReserve({ ...reserve, endAction: 'house_ad' })}>Run “spot available”</Pill>
              <Pill on={reserve.endAction === 'remind'} onClick={() => setReserve({ ...reserve, endAction: 'remind' })}>Remind me to renew</Pill>
            </span></div>
            <p className="nl-pc__help">{reserveHint(reserve.endAction)}</p>
            {/* Ours: what this booking pays, and the artwork that runs (checked against the spot). */}
            <div className="nl-pc__kv"><span>Rate</span><span className="nl-rate">$<input type="number" min={0} aria-label="Rate for this booking" value={reserve.rate} onChange={(e) => setReserve({ ...reserve, rate: e.target.value })} />/issue</span></div>
            <div className="nl-pc__kv"><span>Artwork</span><label className="gg-btn gg-btn--sm">{reserve.creative ? 'Replace image' : 'Upload image'}<input type="file" accept="image/*" hidden onChange={(e) => void pickCreative(e.target.files?.[0])} /></label></div>
            {reserve.warnings.map((w) => <p key={w} className="gg-help nl-warn">{w}</p>)}
            {reserve.creative && reserve.warnings.length === 0 && <p className="gg-ok">The artwork fits this spot.</p>}
            {err && <p className="gg-error">{err}</p>}
            <div className="nl-send__foot">
              <span />
              <button className="gg-btn" onClick={() => setReserve(null)}>Cancel</button>
              <button className="gg-btn gg-btn--amber" disabled={(!reserve.sponsorId && !reserve.newName.trim()) || reserve.ends < reserve.starts || saveBooking.isPending} onClick={() => void confirmReserve(reserve)}>Reserve</button>
            </div>
          </div>
        </Modal>
      )}
      {toast.node}
    </div>
  )
}

function addDays(iso: string, n: number) {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}
